import { friendlyDbError } from '../utils/friendlyDbError';
import { planImageSync } from '../utils/productImageSync';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { WarehouseService } from '../services/warehouseService';
import { createWarehouseError, type WarehouseError } from '../errors/warehouseErrors';
import { autoSyncService } from '../services/autoSyncService';
import { writeWithSchemaFallback } from '../utils/supabaseSchemaFallback';
import type {
  Product,
  ProductFormData,
  ProductFilter,
  BulkProductOperation,
  ProductImportData,
  InventoryStats,
  ProductVariation
} from '../types';
import { productSearchFilter } from '../utils/productSearch';
import { fetchInChunks } from '../utils/inChunks';
import { generateNextSKU } from '../services/productService';

interface ProductStore {
  // State
  products: Product[];
  currentProduct: Product | null;
  selectedProducts: string[];
  loading: boolean;
  error: string | null;
  
  // Pagination
  total: number;
  currentPage: number;
  itemsPerPage: number;
  
  // Filters and Search
  filters: ProductFilter;
  searchQuery: string;
  
  // Stats
  stats: InventoryStats | null;
  statsLoading: boolean;
  
  // Actions
  fetchProducts: () => Promise<void>;
  fetchProduct: (id: string) => Promise<void>;
  fetchProductById: (id: string) => Promise<Product | null>;
  fetchProductBySKU: (skuOrBarcode: string) => Promise<(Product & { matchedVariation?: ProductVariation }) | null>;
  createProduct: (data: ProductFormData) => Promise<Product | null>;
  duplicateProduct: (id: string) => Promise<Product | null>;
  updateProduct: (id: string, data: Partial<ProductFormData>) => Promise<Product | null>;
  deleteProduct: (id: string) => Promise<boolean>;

  // Dependency check
  checkProductDependencies: (ids: string[]) => Promise<Array<{ product_id: string; product_name: string; dependencies: Array<{ label: string; count: number }> }>>;

  // Bulk operations
  bulkOperation: (operation: BulkProductOperation) => Promise<boolean>;
  
  // Selection
  selectProduct: (id: string) => void;
  selectAllProducts: () => void;
  deselectProduct: (id: string) => void;
  clearSelection: () => void;
  
  // Filters
  setFilters: (filters: Partial<ProductFilter>) => void;
  clearFilters: () => void;
  setSearchQuery: (query: string) => void;
  
  // Pagination
  setPage: (page: number) => void;
  setItemsPerPage: (items: number) => void;
  
  // Stats
  fetchStats: () => Promise<void>;
  
  // Import/Export
  importProducts: (data: ProductImportData[]) => Promise<{ success: number; errors: string[] }>;
  exportProducts: (filters?: ProductFilter) => Promise<Blob>;
  
  // Stock operations
  updateStock: (productId: string, quantity: number, reason?: string, notes?: string, variationId?: string) => Promise<boolean>;
  updateStockAbsolute: (productId: string, newQuantity: number, reason?: string, notes?: string) => Promise<boolean>;

  // Variation operations
  updateVariation: (variationId: string, data: Partial<ProductVariation>) => Promise<boolean>;

  // Reset
  reset: () => void;
}

const initialState = {
  products: [],
  currentProduct: null,
  selectedProducts: [],
  loading: false,
  error: null,
  total: 0,
  currentPage: 1,
  itemsPerPage: 100, // Default 100 prodotti per pagina
  filters: {},
  searchQuery: '',
  stats: null,
  statsLoading: false,
};

export const useProductStore = create<ProductStore>((set, get) => ({
  ...initialState,

  fetchProducts: async () => {
    try {
      set({ loading: true, error: null });
      
      const { filters, searchQuery, currentPage, itemsPerPage } = get();
      console.log('Fetching products with search:', searchQuery, 'filters:', filters);

      // Get user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      // Filtrare i prodotti per un attributo richiede che l'embed sia `!inner`:
      // senza, PostgREST filtrerebbe solo le righe annidate e restituirebbe
      // comunque TUTTI i prodotti, ciascuno col suo elenco attributi svuotato.
      // L'inner join si mette solo quando serve, per non cambiare il risultato
      // delle liste normali (un prodotto senza attributi sparirebbe).
      const filtraPerAttributo = !!filters.attribute_name;
      const embedAttributi = filtraPerAttributo
        ? 'attributes:product_attributes!inner(*)'
        : 'attributes:product_attributes(*)';

      let query = supabase
        .from('products')
        .select(`
          *,
          categories:product_category_relations(
            category:product_categories(*)
          ),
          tags:product_tag_relations(
            tag:product_tags(*)
          ),
          images:product_images(*),
          files:product_files(*),
          ${embedAttributi},
          variations:product_variations(*)
        `, { count: 'exact' })
        .eq('organization_id', profile.organization_id);

      if (filters.attribute_name) {
        query = query.eq('attributes.name', filters.attribute_name);
        if (filters.attribute_value) {
          query = query.eq('attributes.value', filters.attribute_value);
        }
      }

      // Apply search (name and sku)
      if (searchQuery && searchQuery.trim()) {
        const searchTerm = searchQuery.trim();
        console.log('Applying search for term:', searchTerm);
        query = query.or(productSearchFilter(searchTerm));
      }

      // Apply filters
      console.log('Applying filters:', filters);
      if (filters.status?.length) {
        console.log('Filtering by status:', filters.status);
        query = query.in('status', filters.status);
      }
      if (filters.type?.length) {
        query = query.in('type', filters.type);
      }
      if (filters.stock_status?.length) {
        query = query.in('stock_status', filters.stock_status);
      }
      if (filters.featured !== undefined) {
        query = query.eq('featured', filters.featured);
      }
      if (filters.price_min !== undefined) {
        query = query.gte('regular_price', filters.price_min);
      }
      if (filters.price_max !== undefined) {
        query = query.lte('regular_price', filters.price_max);
      }
      if (filters.stock_min !== undefined) {
        query = query.gte('stock_quantity', filters.stock_min);
      }
      if (filters.stock_max !== undefined) {
        query = query.lte('stock_quantity', filters.stock_max);
      }
      if (filters.unit_of_measure_1) {
        query = query.eq('unit_of_measure_1', filters.unit_of_measure_1);
      }
      if (filters.family_id) {
        query = query.eq('family_id', filters.family_id);
      }
      if (filters.commercial_division_id) {
        query = query.eq('commercial_division_id', filters.commercial_division_id);
      }
      if (filters.vat_rate !== undefined) {
        query = query.eq('vat_rate', filters.vat_rate);
      }
      if (filters.cost_min !== undefined) {
        query = query.gte('cost_price', filters.cost_min);
      }
      if (filters.cost_max !== undefined) {
        query = query.lte('cost_price', filters.cost_max);
      }
      if (filters.is_sellable !== undefined) {
        query = query.eq('is_sellable', filters.is_sellable);
      }
      if (filters.is_blocked !== undefined) {
        query = query.eq('is_blocked', filters.is_blocked);
      }

      // Apply category filter
      if (filters.category_ids?.length) {
        console.log('Filtering by categories:', filters.category_ids);
        // Get product IDs that have relations with the specified categories
        const { data: categoryProducts } = await supabase
          .from('product_category_relations')
          .select('product_id')
          .in('category_id', filters.category_ids);

        if (categoryProducts && categoryProducts.length > 0) {
          const productIds = categoryProducts.map(cp => cp.product_id);
          query = query.in('id', productIds);
        } else {
          // No products found for these categories, return empty result
          query = query.eq('id', '00000000-0000-0000-0000-000000000000'); // Non-existent ID
        }
      }

      // Apply sorting
      const sortBy = filters.sort_by || 'created_at';
      const sortOrder = filters.sort_order || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Carica tutti i prodotti (l'infinite scroll client-side gestisce il rendering progressivo)
      const { data, error, count } = await query;

      if (error) throw error;

      // Transform data to include computed fields
      const transformedProducts: Product[] = (data || []).map(product => ({
        ...product,
        categories: product.categories?.map(rel => rel.category) || [],
        category_names: product.categories?.map(rel => rel.category.name).join(', ') || '',
        tags: product.tags?.map(rel => rel.tag) || [],
        main_image: product.images?.find(img => img.is_main) || product.images?.[0],
        alert_level: product.stock_quantity <= 0 ? 'out_of_stock' as const :
                    product.stock_quantity <= product.low_stock_threshold ? 'low_stock' as const :
                    'in_stock' as const
      }));

      set({ 
        products: transformedProducts, 
        total: count || 0,
        loading: false 
      });

    } catch (error) {
      console.error('Error fetching products:', error);
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  fetchProductById: async (id: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories:product_category_relations(
            category:product_categories(*)
          ),
          tags:product_tag_relations(
            tag:product_tags(*)
          ),
          images:product_images(*),
          files:product_files(*),
          attributes:product_attributes(*),
          variations:product_variations(
            *,
            attributes:variation_attributes(*)
          )
        `)
        .eq('id', id)
        .eq('organization_id', profile.organization_id)
        .single();

      if (error) throw error;

      if (data) {
        const transformedProduct: Product = {
          ...data,
          categories: data.categories?.map(rel => rel.category) || [],
          category_names: data.categories?.map(rel => rel.category.name).join(', ') || '',
          tags: data.tags?.map(rel => rel.tag) || [],
          main_image: data.images?.find(img => img.is_main) || data.images?.[0],
          alert_level: data.stock_quantity <= 0 ? 'out_of_stock' as const :
                      data.stock_quantity <= data.low_stock_threshold ? 'low_stock' as const :
                      'in_stock' as const
        };

        return transformedProduct;
      }

      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  fetchProduct: async (id: string) => {
    try {
      set({ loading: true, error: null, currentProduct: null });
      
      const product = await get().fetchProductById(id);
      
      set({ 
        currentProduct: product, 
        loading: false 
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      set({ 
        error: friendlyDbError(error), 
        loading: false,
        currentProduct: null 
      });
    }
  },

  fetchProductBySKU: async (skuOrBarcode: string) => {
    try {
      console.log('Fetching product by SKU/Barcode:', skuOrBarcode);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      const organizationId = profile?.organization_id;
      if (!organizationId) throw new Error('Organization not found');

      // Query base per il prodotto con tutte le relazioni
      const productQuery = `
        *,
        categories:product_category_relations(
          category:product_categories(*)
        ),
        tags:product_tag_relations(
          tag:product_tags(*)
        ),
        images:product_images(*),
        files:product_files(*),
        attributes:product_attributes(*),
        variations:product_variations(
          *,
          attributes:variation_attributes(*)
        )
      `;

      // Helper per trasformare il prodotto
      const transformProduct = (data: any, matchedVariation?: any): Product & { matchedVariation?: ProductVariation } => {
        const product: Product & { matchedVariation?: ProductVariation } = {
          ...data,
          categories: data.categories?.map((rel: any) => rel.category) || [],
          category_names: data.categories?.map((rel: any) => rel.category.name).join(', ') || '',
          tags: data.tags?.map((rel: any) => rel.tag) || [],
          main_image: data.images?.find((img: any) => img.is_main) || data.images?.[0],
          alert_level: data.stock_quantity <= 0 ? 'out_of_stock' as const :
                      data.stock_quantity <= data.low_stock_threshold ? 'low_stock' as const :
                      'in_stock' as const
        };
        if (matchedVariation) {
          product.matchedVariation = matchedVariation;
        }
        return product;
      };

      // 1. Cerca per SKU prodotto
      let { data, error } = await supabase
        .from('products')
        .select(productQuery)
        .eq('sku', skuOrBarcode)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (!error && data) {
        console.log('Product found by SKU:', data.name);
        return transformProduct(data);
      }

      // 2. Se SKU contiene apostrofo, prova con trattino (fallback scanner)
      if (skuOrBarcode.includes("'")) {
        const skuWithDash = skuOrBarcode.replace(/'/g, '-');
        console.log('Trying fallback SKU:', skuWithDash);

        const fallbackResult = await supabase
          .from('products')
          .select(productQuery)
          .eq('sku', skuWithDash)
          .eq('organization_id', organizationId)
          .maybeSingle();

        if (!fallbackResult.error && fallbackResult.data) {
          console.log('Product found with fallback SKU:', fallbackResult.data.name);
          return transformProduct(fallbackResult.data);
        }
      }

      // 3. Cerca per barcode prodotto
      const { data: barcodeData, error: barcodeError } = await supabase
        .from('products')
        .select(productQuery)
        .eq('barcode', skuOrBarcode)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (!barcodeError && barcodeData) {
        console.log('Product found by barcode:', barcodeData.name);
        return transformProduct(barcodeData);
      }

      // 4. Cerca per SKU variante
      const { data: variationBySku, error: variationSkuError } = await supabase
        .from('product_variations')
        .select(`
          *,
          attributes:variation_attributes(*),
          product:products!inner(${productQuery})
        `)
        .eq('sku', skuOrBarcode)
        .eq('product.organization_id', organizationId)
        .maybeSingle();

      if (!variationSkuError && variationBySku) {
        console.log('Variation found by SKU:', skuOrBarcode, '- Product:', variationBySku.product.name);
        const variation: ProductVariation = {
          id: variationBySku.id,
          product_id: variationBySku.product_id,
          sku: variationBySku.sku,
          barcode: variationBySku.barcode,
          regular_price: variationBySku.regular_price,
          sale_price: variationBySku.sale_price,
          cost_price: variationBySku.cost_price,
          stock_quantity: variationBySku.stock_quantity,
          stock_status: variationBySku.stock_status,
          weight: variationBySku.weight,
          length: variationBySku.length,
          width: variationBySku.width,
          height: variationBySku.height,
          image_url: variationBySku.image_url,
          image_alt: variationBySku.image_alt,
          sort_order: variationBySku.sort_order,
          attributes: variationBySku.attributes || [],
          woocommerce_variation_id: variationBySku.woocommerce_variation_id,
          created_at: variationBySku.created_at,
          updated_at: variationBySku.updated_at
        };
        return transformProduct(variationBySku.product, variation);
      }

      // 5. Cerca per barcode variante
      const { data: variationByBarcode, error: variationBarcodeError } = await supabase
        .from('product_variations')
        .select(`
          *,
          attributes:variation_attributes(*),
          product:products!inner(${productQuery})
        `)
        .eq('barcode', skuOrBarcode)
        .eq('product.organization_id', organizationId)
        .maybeSingle();

      if (!variationBarcodeError && variationByBarcode) {
        console.log('Variation found by barcode:', skuOrBarcode, '- Product:', variationByBarcode.product.name);
        const variation: ProductVariation = {
          id: variationByBarcode.id,
          product_id: variationByBarcode.product_id,
          sku: variationByBarcode.sku,
          barcode: variationByBarcode.barcode,
          regular_price: variationByBarcode.regular_price,
          sale_price: variationByBarcode.sale_price,
          cost_price: variationByBarcode.cost_price,
          stock_quantity: variationByBarcode.stock_quantity,
          stock_status: variationByBarcode.stock_status,
          weight: variationByBarcode.weight,
          length: variationByBarcode.length,
          width: variationByBarcode.width,
          height: variationByBarcode.height,
          image_url: variationByBarcode.image_url,
          image_alt: variationByBarcode.image_alt,
          sort_order: variationByBarcode.sort_order,
          attributes: variationByBarcode.attributes || [],
          woocommerce_variation_id: variationByBarcode.woocommerce_variation_id,
          created_at: variationByBarcode.created_at,
          updated_at: variationByBarcode.updated_at
        };
        return transformProduct(variationByBarcode.product, variation);
      }

      console.log('Product/Variation not found by SKU/Barcode:', skuOrBarcode);
      return null;
    } catch (error) {
      console.error('Error fetching product by SKU/Barcode:', error);
      return null;
    }
  },

  createProduct: async (data: ProductFormData) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      // Generate unique slug
      const generateSlug = (name: string): string => {
        return name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      };

      const generateUniqueSlug = async (baseName: string): Promise<string> => {
        const baseSlug = generateSlug(baseName) || 'prodotto';
        
        // Never allow empty slug - always ensure we have a valid base
        if (!baseSlug || baseSlug.length === 0) {
          console.warn('Generated empty slug, using default');
          return await generateUniqueSlug('prodotto');
        }
        
        let slug = baseSlug;
        let counter = 1;

        while (true) {
          // Check for both exact match and empty string (to handle existing empty slugs)
          const { count } = await supabase
            .from('products')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', profile.organization_id)
            .eq('slug', slug);

          if (count === 0) break;
          
          slug = `${baseSlug}-${counter}`;
          counter++;
          
          // Prevent infinite loops
          if (counter > 1000) {
            slug = `${baseSlug}-${Date.now()}`;
            break;
          }
        }

        console.log(`Generated unique slug: "${slug}" from base: "${baseName}"`);
        return slug;
      };

      // Separate product data from relations
      const { category_ids, tag_ids, attributes, attributeGroups, variations, images, files, ...productData } = data;

      console.log('Product data received:', { 
        name: productData.name, 
        slug: productData.slug,
        keys: Object.keys(productData) 
      });
      
      // Generate unique slug - prioritize name, fallback to existing slug if available, otherwise default
      const baseName = productData.name?.trim() || productData.slug?.trim() || 'Nuovo Prodotto';
      console.log('Base name for slug generation:', baseName);
      
      const uniqueSlug = await generateUniqueSlug(baseName);
      
      // Clean empty string timestamps and ensure no empty strings
      const cleanedProductData = {
        ...productData,
        name: productData.name?.trim() || 'Nuovo Prodotto',
        slug: uniqueSlug,
        sku: productData.sku?.trim() || null,
        description: productData.description?.trim() || '',
        short_description: productData.short_description?.trim() || '',
        sale_price_from: productData.sale_price_from || null,
        sale_price_to: productData.sale_price_to || null,
        // FK campi estesi: empty string → null (uuid non accetta '')
        family_id: (productData as any).family_id || null,
        commercial_division_id: (productData as any).commercial_division_id || null,
        commercial_division_2_id: (productData as any).commercial_division_2_id || null,
        commercial_division_3_id: (productData as any).commercial_division_3_id || null,
        brand_id: (productData as any).brand_id || null,
        // La giacenza iniziale entra solo dal movimento sotto: scriverla anche qui la raddoppia.
        stock_quantity: 0,
      };

      // Create product. Stesso retry omettendo colonne non ancora migrate
      // (vedi commento in updateProduct).
      const { data: product, error: productError } = await writeWithSchemaFallback(
        (payload) => supabase.from('products').insert(payload).select().single(),
        {
          ...cleanedProductData,
          organization_id: profile.organization_id,
          created_by: userData.user.id,
          updated_by: userData.user.id,
        }
      );

      if (productError) throw productError;

      // Create category relations
      if (category_ids.length > 0) {
        const categoryRelations = category_ids.map(categoryId => ({
          product_id: product.id,
          category_id: categoryId
        }));

        const { error: categoryError } = await supabase
          .from('product_category_relations')
          .insert(categoryRelations);

        if (categoryError) throw categoryError;
      }

      // Create tag relations
      if (tag_ids.length > 0) {
        const tagRelations = tag_ids.map(tagId => ({
          product_id: product.id,
          tag_id: tagId
        }));

        const { error: tagError } = await supabase
          .from('product_tag_relations')
          .insert(tagRelations);

        if (tagError) throw tagError;
      }

      // Create attributes (legacy system)
      if (attributes.length > 0) {
        // Come in updateProduct: solo le colonne della tabella e mai l'id, che
        // per le righe create nel form e' una stringa tipo "attr-1739..." e non
        // un uuid. Passarlo faceva fallire l'insert con 22P02.
        const attributeData = attributes.map((attr: any, i: number) => ({
          product_id: product.id,
          name: attr.name,
          value: attr.value,
          variation: attr.variation ?? false,
          visible: attr.visible ?? true,
          position: attr.position ?? i,
        }));

        const { error: attributeError } = await supabase
          .from('product_attributes')
          .insert(attributeData);

        if (attributeError) throw attributeError;
      }

      // Create attribute groups and variations (new system)
      if (attributeGroups && attributeGroups.length > 0) {
        // Salva i gruppi di attributi come attributi legacy per compatibilità
        const attributeGroupsData = attributeGroups.flatMap(group => 
          group.values.filter(v => v.trim()).map((value, index) => ({
            product_id: product.id,
            name: group.name,
            value: value,
            variation: group.forVariations,
            visible: group.visible,
            position: group.position * 100 + index
          }))
        );

        if (attributeGroupsData.length > 0) {
          const { error: groupAttributeError } = await supabase
            .from('product_attributes')
            .insert(attributeGroupsData);

          if (groupAttributeError) throw groupAttributeError;
        }

        // Create variations if present
        if (variations && variations.length > 0) {
          const enabledVariations = variations.filter(v => v.enabled);
          
          for (const variation of enabledVariations) {
            // Create variation record with all WooCommerce fields
            const { data: variationRecord, error: variationError } = await supabase
              .from('product_variations')
              .insert({
                product_id: product.id,
                sku: variation.sku,
                barcode: variation.barcode || null,
                regular_price: variation.regular_price,
                sale_price: variation.sale_price,
                cost_price: variation.cost_price,
                stock_quantity: variation.stock_quantity,
                // Calcola automaticamente stock_status in base alla quantità
                stock_status: (variation.stock_quantity || 0) > 0 ? 'instock' : 'outofstock',
                weight: variation.weight || null,
                length: variation.length || null,
                width: variation.width || null,
                height: variation.height || null,
                sort_order: 0,
                image_url: variation.image_url || null,
                image_alt: variation.image_alt || null,
                // Campi WooCommerce aggiuntivi
                enabled: variation.enabled ?? true,
                downloadable: variation.downloadable || false,
                virtual: variation.virtual || false,
                manage_stock: variation.manage_stock ?? true,
                backorders: variation.backorders || 'no',
                low_stock_threshold: variation.low_stock_threshold || null,
                shipping_class: variation.shipping_class || null,
                tax_class: variation.tax_class || null,
                description: variation.description || null
              })
              .select()
              .single();

            if (variationError) throw variationError;

            // Create variation attributes
            const variationAttributes = Object.entries(variation.attributes).map(([name, value]) => ({
              variation_id: variationRecord.id,
              attribute_name: name,
              attribute_value: value
            }));

            const { error: variationAttributeError } = await supabase
              .from('variation_attributes')
              .insert(variationAttributes);

            if (variationAttributeError) throw variationAttributeError;
          }
        }
      }

      // Create product images
      if (images && images.length > 0) {
        const imageData = images.map(img => ({
          product_id: product.id,
          filename: img.url.split('/').pop() || 'image',
          file_path: img.url,
          alt_text: img.alt_text || '',
          is_main: img.is_main || false,
          position: img.sort_order || 0
        }));

        const { error: imagesError } = await supabase
          .from('product_images')
          .insert(imageData);

        if (imagesError) throw imagesError;
      }

      // Create product files (downloadable)
      if (files && files.length > 0) {
        const fileData = files.map((file, index) => ({
          product_id: product.id,
          organization_id: profile.organization_id,
          filename: file.url.split('/').pop() || 'file',
          original_name: file.original_name,
          file_path: file.url,
          file_size: file.file_size || null,
          mime_type: file.mime_type || null,
          display_name: file.display_name || file.original_name,
          position: file.position ?? index
        }));

        const { error: filesError } = await supabase
          .from('product_files')
          .insert(fileData);

        if (filesError) throw filesError;
      }

      // Create initial stock movement
      if (data.stock_quantity > 0) {
        const { error: stockError } = await supabase.rpc('create_stock_movement', {
          p_product_id: product.id,
          p_type: 'in',
          p_quantity: data.stock_quantity,
          p_reason: 'initial_stock',
          p_notes: 'Stock iniziale alla creazione prodotto'
        });
        if (stockError) throw stockError;
      }

      set({ loading: false });

      // Refresh products list
      get().fetchProducts();

      // Trigger auto-sync for product creation
      if (product?.id) {
        autoSyncService.triggerSync('product', product.id, 'create');
      }

      return product as Product;

    } catch (error) {
      console.error('Error creating product:', error);
      set({ error: friendlyDbError(error), loading: false });
      return null;
    }
  },

  duplicateProduct: async (id: string) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      // Prodotto sorgente (riga completa)
      const { data: source, error: sourceError } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .eq('organization_id', profile.organization_id)
        .single();

      if (sourceError) throw sourceError;
      if (!source) throw new Error('Prodotto non trovato');

      const {
        id: _id,
        created_at: _createdAt,
        updated_at: _updatedAt,
        ...sourceData
      } = source;

      const newName = `${source.name} (Copia)`;
      // Slug unico senza loop di verifica: suffisso timestamp base36
      const newSlug = `${source.slug || 'prodotto'}-copia-${Date.now().toString(36)}`;

      // Lo SKU e' obbligatorio nel form: la copia ne riceve uno nuovo dal contatore dell'organizzazione
      const skuResult = await generateNextSKU({ organizationId: profile.organization_id });
      if (skuResult.error || !skuResult.sku) {
        throw new Error(skuResult.error || 'Impossibile generare lo SKU della copia');
      }

      const { data: newProduct, error: insertError } = await supabase
        .from('products')
        .insert({
          ...sourceData,
          name: newName,
          slug: newSlug,
          // La copia nasce in Bozza: evita pubblicazione/sync accidentale prima della revisione
          status: 'draft',
          // SKU e barcode devono restare univoci: la copia prende un SKU nuovo e parte senza barcode
          sku: skuResult.sku,
          barcode: null,
          sku_auto_generated: true,
          barcode_auto_generated: false,
          // La copia parte senza stock: niente movimenti né stoccaggi ereditati
          stock_quantity: 0,
          stock_status: 'outofstock',
          created_by: userData.user.id,
          updated_by: userData.user.id
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Copia relazioni categorie e tag
      const { data: categoryRels } = await supabase
        .from('product_category_relations')
        .select('category_id')
        .eq('product_id', id);
      if (categoryRels && categoryRels.length > 0) {
        await supabase.from('product_category_relations').insert(
          categoryRels.map(rel => ({ product_id: newProduct.id, category_id: rel.category_id }))
        );
      }

      const { data: tagRels } = await supabase
        .from('product_tag_relations')
        .select('tag_id')
        .eq('product_id', id);
      if (tagRels && tagRels.length > 0) {
        await supabase.from('product_tag_relations').insert(
          tagRels.map(rel => ({ product_id: newProduct.id, tag_id: rel.tag_id }))
        );
      }

      // Copia attributi
      const { data: sourceAttributes } = await supabase
        .from('product_attributes')
        .select('*')
        .eq('product_id', id);
      if (sourceAttributes && sourceAttributes.length > 0) {
        await supabase.from('product_attributes').insert(
          sourceAttributes.map(({ id: _attrId, created_at: _c, updated_at: _u, ...attr }) => ({
            ...attr,
            product_id: newProduct.id
          }))
        );
      }

      // Copia immagini
      const { data: sourceImages } = await supabase
        .from('product_images')
        .select('*')
        .eq('product_id', id);
      if (sourceImages && sourceImages.length > 0) {
        await supabase.from('product_images').insert(
          sourceImages.map(({ id: _imgId, created_at: _c, updated_at: _u, ...img }) => ({
            ...img,
            product_id: newProduct.id
          }))
        );
      }

      // Copia varianti (senza SKU/barcode e senza stock) con relativi attributi
      const { data: sourceVariations } = await supabase
        .from('product_variations')
        .select('*, attributes:variation_attributes(*)')
        .eq('product_id', id);
      for (const variation of sourceVariations || []) {
        const { id: _variationId, created_at: _c, updated_at: _u, attributes: variationAttributes, ...variationData } = variation;

        const { data: newVariation, error: variationError } = await supabase
          .from('product_variations')
          .insert({
            ...variationData,
            product_id: newProduct.id,
            sku: null,
            barcode: null,
            stock_quantity: 0,
            stock_status: 'outofstock'
          })
          .select()
          .single();

        if (variationError) throw variationError;

        if (variationAttributes && variationAttributes.length > 0) {
          await supabase.from('variation_attributes').insert(
            variationAttributes.map(({ id: _vaId, created_at: _vc, ...attr }: any) => ({
              ...attr,
              variation_id: newVariation.id
            }))
          );
        }
      }

      set({ loading: false });

      // Refresh products list
      get().fetchProducts();

      return newProduct as Product;

    } catch (error) {
      console.error('Error duplicating product:', error);
      set({ error: friendlyDbError(error), loading: false });
      return null;
    }
  },

  updateProduct: async (id: string, data: Partial<ProductFormData>) => {
    try {
      set({ loading: true, error: null });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Separate product data from relations
      const { category_ids, tag_ids, attributes, attributeGroups, variations, images, files, ...productData } = data;

      // Clean empty string timestamps (convert to null)
      const cleanedProductData = {
        ...productData,
        sale_price_from: productData.sale_price_from || null,
        sale_price_to: productData.sale_price_to || null,
        // FK campi estesi: empty string → null (uuid non accetta '')
        family_id: (productData as any).family_id || null,
        commercial_division_id: (productData as any).commercial_division_id || null,
        commercial_division_2_id: (productData as any).commercial_division_2_id || null,
        commercial_division_3_id: (productData as any).commercial_division_3_id || null,
        brand_id: (productData as any).brand_id || null,
      };

      // SKU/barcode vuoti → null (unique constraint: '' collide tra prodotti, null no)
      if (cleanedProductData.sku !== undefined) {
        (cleanedProductData as Record<string, unknown>).sku = cleanedProductData.sku?.trim() || null;
      }
      if (cleanedProductData.barcode !== undefined) {
        (cleanedProductData as Record<string, unknown>).barcode = cleanedProductData.barcode?.trim() || null;
      }

      // stock_quantity non va mai scritto nudo in update: se cambiato, passa dalla
      // rettifica zone-aware (allinea le zone e registra il movimento nello storico)
      let stockAdjustTo: number | undefined;
      if (cleanedProductData.stock_quantity !== undefined) {
        const { data: currentRow } = await supabase
          .from('products')
          .select('stock_quantity')
          .eq('id', id)
          .single();

        if (currentRow && currentRow.stock_quantity !== cleanedProductData.stock_quantity) {
          stockAdjustTo = cleanedProductData.stock_quantity as number;
        }
        delete (cleanedProductData as Partial<ProductFormData>).stock_quantity;
      }

      // Update product. Retry omettendo le colonne non ancora migrate (Wave 4a
      // ha aggiunto molti campi opzionali via migration additiva: se una non è
      // ancora stata eseguita, il salvataggio degrada invece di bloccarsi tutto).
      const { data: product, error: productError } = await writeWithSchemaFallback(
        (payload) => supabase.from('products').update(payload).eq('id', id).select().single(),
        { ...cleanedProductData, updated_by: userData.user.id }
      );

      if (productError) throw productError;

      if (stockAdjustTo !== undefined) {
        await WarehouseService.adjustProductStock(
          id,
          stockAdjustTo,
          'manual_adjustment',
          'Rettifica quantità da modifica prodotto'
        );
        product.stock_quantity = stockAdjustTo;
      }

      // Update category relations if provided
      if (category_ids !== undefined) {
        // Delete existing relations
        await supabase
          .from('product_category_relations')
          .delete()
          .eq('product_id', id);

        // Create new relations
        if (category_ids.length > 0) {
          const categoryRelations = category_ids.map(categoryId => ({
            product_id: id,
            category_id: categoryId
          }));

          await supabase
            .from('product_category_relations')
            .insert(categoryRelations);
        }
      }

      // Update tag relations if provided
      if (tag_ids !== undefined) {
        // Delete existing relations
        await supabase
          .from('product_tag_relations')
          .delete()
          .eq('product_id', id);

        // Create new relations
        if (tag_ids.length > 0) {
          const tagRelations = tag_ids.map(tagId => ({
            product_id: id,
            tag_id: tagId
          }));

          await supabase
            .from('product_tag_relations')
            .insert(tagRelations);
        }
      }

      // Update attributes if provided (legacy system)
      if (attributes !== undefined) {
        // Delete existing attributes
        await supabase
          .from('product_attributes')
          .delete()
          .eq('product_id', id);

        // Create new attributes
        if (attributes.length > 0) {
          // Solo le colonne della tabella, e MAI l'id: quelli delle righe
          // aggiunte nel form sono stringhe tipo "attr-1739..." e non uuid.
          // Passandoli, l'insert fallisce con 22P02 — e siccome la delete qui
          // sopra e' gia' avvenuta, il prodotto resterebbe senza alcun attributo.
          const attributeData = attributes.map((attr: any, i: number) => ({
            product_id: id,
            name: attr.name,
            value: attr.value,
            variation: attr.variation ?? false,
            visible: attr.visible ?? true,
            position: attr.position ?? i,
          }));

          const { error: attrError } = await supabase
            .from('product_attributes')
            .insert(attributeData);
          if (attrError) throw attrError;
        }
      }

      // Update attribute groups and variations (new system)
      if (attributeGroups !== undefined) {
        // Recupera le varianti esistenti con i loro ID per preservarli
        const { data: existingVariations } = await supabase
          .from('product_variations')
          .select('id, sku')
          .eq('product_id', id);

        console.log('[updateProduct] Varianti esistenti nel DB:', existingVariations?.map(v => ({ id: v.id, sku: v.sku })));
        console.log('[updateProduct] Varianti dal form:', variations?.map(v => ({ id: v.id, sku: v.sku, enabled: v.enabled })));

        // Crea una mappa SKU -> ID per le varianti esistenti
        const existingVariationMap = new Map<string, string>(
          (existingVariations || []).map(v => [v.sku, v.id])
        );

        console.log('[updateProduct] Mappa SKU -> ID:', Object.fromEntries(existingVariationMap));

        // Delete existing attribute groups (stored as regular attributes with variation flag)
        await supabase
          .from('product_attributes')
          .delete()
          .eq('product_id', id);

        // Recreate attribute groups
        if (attributeGroups.length > 0) {
          const attributeGroupsData = attributeGroups.flatMap(group =>
            group.values.filter(v => v.trim()).map((value, index) => ({
              product_id: id,
              name: group.name,
              value: value,
              variation: group.forVariations,
              visible: group.visible,
              position: group.position * 100 + index
            }))
          );

          if (attributeGroupsData.length > 0) {
            await supabase
              .from('product_attributes')
              .insert(attributeGroupsData);
          }

          // Gestione varianti: UPDATE esistenti, INSERT nuove, DELETE rimosse
          if (variations && variations.length > 0) {
            const enabledVariations = variations.filter(v => v.enabled);
            const processedVariationIds: string[] = [];

            for (const variation of enabledVariations) {
              // Cerca se la variante esiste già (per ID o per SKU)
              const existingId = variation.id || existingVariationMap.get(variation.sku);

              console.log(`[updateProduct] Variante SKU=${variation.sku}: variation.id=${variation.id}, existingId=${existingId}`);

              if (existingId) {
                // UPDATE: La variante esiste - aggiorna preservando l'ID
                console.log(`[updateProduct] UPDATE variante ${existingId}`);
                const { error: updateError } = await supabase
                  .from('product_variations')
                  .update({
                    sku: variation.sku,
                    barcode: variation.barcode || null,
                    regular_price: variation.regular_price,
                    sale_price: variation.sale_price,
                    cost_price: variation.cost_price,
                    stock_quantity: variation.stock_quantity,
                    // Calcola automaticamente stock_status in base alla quantità
                    stock_status: (variation.stock_quantity || 0) > 0 ? 'instock' : 'outofstock',
                    weight: variation.weight || null,
                    length: variation.length || null,
                    width: variation.width || null,
                    height: variation.height || null,
                    image_url: variation.image_url || null,
                    image_alt: variation.image_alt || null,
                    enabled: variation.enabled ?? true,
                    downloadable: variation.downloadable || false,
                    virtual: variation.virtual || false,
                    manage_stock: variation.manage_stock ?? true,
                    backorders: variation.backorders || 'no',
                    low_stock_threshold: variation.low_stock_threshold || null,
                    shipping_class: variation.shipping_class || null,
                    tax_class: variation.tax_class || null,
                    description: variation.description || null
                  })
                  .eq('id', existingId);

                if (updateError) throw updateError;

                // Aggiorna gli attributi della variante
                await supabase
                  .from('variation_attributes')
                  .delete()
                  .eq('variation_id', existingId);

                const variationAttributes = Object.entries(variation.attributes).map(([name, value]) => ({
                  variation_id: existingId,
                  attribute_name: name,
                  attribute_value: value
                }));

                await supabase
                  .from('variation_attributes')
                  .insert(variationAttributes);

                processedVariationIds.push(existingId);
              } else {
                // INSERT: Nuova variante
                console.log(`[updateProduct] INSERT nuova variante SKU=${variation.sku}`);
                const { data: variationRecord, error: variationError } = await supabase
                  .from('product_variations')
                  .insert({
                    product_id: id,
                    sku: variation.sku,
                    barcode: variation.barcode || null,
                    regular_price: variation.regular_price,
                    sale_price: variation.sale_price,
                    cost_price: variation.cost_price,
                    stock_quantity: variation.stock_quantity,
                    // Calcola automaticamente stock_status in base alla quantità
                    stock_status: (variation.stock_quantity || 0) > 0 ? 'instock' : 'outofstock',
                    weight: variation.weight || null,
                    length: variation.length || null,
                    width: variation.width || null,
                    height: variation.height || null,
                    sort_order: 0,
                    image_url: variation.image_url || null,
                    image_alt: variation.image_alt || null,
                    enabled: variation.enabled ?? true,
                    downloadable: variation.downloadable || false,
                    virtual: variation.virtual || false,
                    manage_stock: variation.manage_stock ?? true,
                    backorders: variation.backorders || 'no',
                    low_stock_threshold: variation.low_stock_threshold || null,
                    shipping_class: variation.shipping_class || null,
                    tax_class: variation.tax_class || null,
                    description: variation.description || null
                  })
                  .select()
                  .single();

                if (variationError) throw variationError;

                // Create variation attributes
                const variationAttributes = Object.entries(variation.attributes).map(([name, value]) => ({
                  variation_id: variationRecord.id,
                  attribute_name: name,
                  attribute_value: value
                }));

                await supabase
                  .from('variation_attributes')
                  .insert(variationAttributes);

                processedVariationIds.push(variationRecord.id);
              }
            }

            // DELETE: Rimuovi le varianti che non esistono più nel form
            const variationsToDelete = (existingVariations || [])
              .filter(v => !processedVariationIds.includes(v.id))
              .map(v => v.id);

            if (variationsToDelete.length > 0) {
              // Prima elimina gli attributi delle varianti
              await supabase
                .from('variation_attributes')
                .delete()
                .in('variation_id', variationsToDelete);

              // Poi elimina le varianti (CASCADE eliminerà anche i prezzi listino)
              await supabase
                .from('product_variations')
                .delete()
                .in('id', variationsToDelete);
            }
          } else {
            // Se non ci sono varianti nel form, elimina tutte quelle esistenti
            if (existingVariations && existingVariations.length > 0) {
              const variationIds = existingVariations.map(v => v.id);

              await supabase
                .from('variation_attributes')
                .delete()
                .in('variation_id', variationIds);

              await supabase
                .from('product_variations')
                .delete()
                .eq('product_id', id);
            }
          }
        }
      }

      // Foto per differenza (lib/utils/productImageSync): una foto gia' salvata tiene la sua riga, cosi'
      // i negozi collegati non la ricaricano a ogni salvataggio
      if (images !== undefined) {
        const { data: savedImages, error: savedError } = await supabase
          .from('product_images')
          .select('id, file_path, alt_text, is_main, position')
          .eq('product_id', id);
        if (savedError) throw savedError;
        const plan = planImageSync(savedImages || [], images);
        if (plan.remove.length > 0) {
          const { error } = await supabase.from('product_images').delete().in('id', plan.remove);
          if (error) throw error;
        }
        for (const u of plan.update) {
          const { error } = await supabase.from('product_images').update(u.values).eq('id', u.id);
          if (error) throw error;
        }
        if (plan.insert.length > 0) {
          const { error: imagesError } = await supabase
            .from('product_images')
            .insert(plan.insert.map((img) => ({ product_id: id, ...img })));
          if (imagesError) throw imagesError;
        }
      }

      // Update product files (downloadable) if provided
      if (files !== undefined) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();

        if (profile) {
          // Delete existing files
          await supabase
            .from('product_files')
            .delete()
            .eq('product_id', id);

          // Create new files
          if (files.length > 0) {
            const fileData = files.map((file, index) => ({
              product_id: id,
              organization_id: profile.organization_id,
              filename: file.url.split('/').pop() || 'file',
              original_name: file.original_name,
              file_path: file.url,
              file_size: file.file_size || null,
              mime_type: file.mime_type || null,
              display_name: file.display_name || file.original_name,
              position: file.position ?? index
            }));

            const { error: filesError } = await supabase
              .from('product_files')
              .insert(fileData);

            if (filesError) throw filesError;
          }
        }
      }

      set({ loading: false });

      // Refresh products list
      get().fetchProducts();

      // Trigger auto-sync for product update
      autoSyncService.triggerSync('product', id, 'update');

      return product as Product;

    } catch (error) {
      console.error('Error updating product:', error);
      set({ error: friendlyDbError(error), loading: false });
      return null;
    }
  },

  deleteProduct: async (id: string) => {
    try {
      set({ loading: true, error: null });

      // Get product SKU before deleting (needed for WooCommerce sync fallback)
      const { data: product } = await supabase
        .from('products')
        .select('sku')
        .eq('id', id)
        .single();

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set({ loading: false });

      // Refresh products list
      get().fetchProducts();

      // Trigger auto-sync for product deletion (pass SKU for fallback search)
      autoSyncService.triggerSync('product', id, 'delete', { sku: product?.sku });

      return true;

    } catch (error) {
      console.error('Error deleting product:', error);
      set({ error: friendlyDbError(error), loading: false });
      return false;
    }
  },

  checkProductDependencies: async (ids: string[]) => {
    try {
      const { data, error } = await supabase.rpc('check_product_dependencies', {
        p_product_ids: ids
      });
      if (error) throw error;
      return (data as Array<{ product_id: string; product_name: string; dependencies: Array<{ label: string; count: number }> }>) || [];
    } catch (error) {
      console.error('Error checking product dependencies:', error);
      return [];
    }
  },

  bulkOperation: async (operation: BulkProductOperation) => {
    try {
      set({ loading: true, error: null });

      switch (operation.action) {
        case 'delete':
          const { error: deleteError } = await supabase
            .from('products')
            .delete()
            .in('id', operation.product_ids);

          if (deleteError) throw deleteError;
          break;

        case 'update_status':
          if (!operation.data?.status) throw new Error('Status is required');
          
          const { error: statusError } = await supabase
            .from('products')
            .update({ status: operation.data.status })
            .in('id', operation.product_ids);

          if (statusError) throw statusError;
          break;

        case 'update_category':
          if (!operation.data?.category_ids?.length) throw new Error('Categories are required');
          
          // Delete existing category relations
          await supabase
            .from('product_category_relations')
            .delete()
            .in('product_id', operation.product_ids);

          // Create new relations
          const categoryRelations = operation.product_ids.flatMap(productId =>
            operation.data!.category_ids!.map(categoryId => ({
              product_id: productId,
              category_id: categoryId
            }))
          );

          const { error: categoryError } = await supabase
            .from('product_category_relations')
            .insert(categoryRelations);

          if (categoryError) throw categoryError;
          break;

        default:
          throw new Error('Unsupported bulk operation');
      }

      set({ loading: false, selectedProducts: [] });

      // Refresh products list
      get().fetchProducts();

      // Trigger auto-sync for each product in the bulk operation
      const syncAction = operation.action === 'delete' ? 'delete' : 'update';
      for (const productId of operation.product_ids) {
        autoSyncService.triggerSync('product', productId, syncAction);
      }

      return true;

    } catch (error) {
      console.error('Error performing bulk operation:', error);
      set({ error: friendlyDbError(error), loading: false });
      return false;
    }
  },

  // Selection methods
  selectProduct: (id: string) => {
    set(state => ({
      selectedProducts: [...state.selectedProducts, id]
    }));
  },

  selectAllProducts: () => {
    set(state => ({
      selectedProducts: state.products.map(p => p.id)
    }));
  },

  deselectProduct: (id: string) => {
    set(state => ({
      selectedProducts: state.selectedProducts.filter(pid => pid !== id)
    }));
  },

  clearSelection: () => {
    set({ selectedProducts: [] });
  },

  // Filter methods
  setFilters: (filters: Partial<ProductFilter>) => {
    console.log('Store: Setting filters to:', filters);
    set(state => ({
      filters: { ...state.filters, ...filters },
      currentPage: 1 // Reset to first page when filtering
    }));
    // fetchProducts() viene chiamato dal MagazzinoMain con debounce
  },

  clearFilters: () => {
    set({ filters: {}, currentPage: 1 });
  },

  setSearchQuery: (query: string) => {
    console.log('Store: Setting search query to:', query);
    set({ searchQuery: query, currentPage: 1 });
    // fetchProducts() viene chiamato dal MagazzinoMain con debounce
  },

  // Pagination methods
  setPage: (page: number) => {
    set({ currentPage: page });
    get().fetchProducts();
  },

  setItemsPerPage: (items: number) => {
    set({ itemsPerPage: items, currentPage: 1 });
    get().fetchProducts();
  },

  // Stats
  fetchStats: async () => {
    try {
      set({ statsLoading: true });

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      // Get basic stats with product IDs for price lookup
      const { data: statsData, error } = await supabase
        .from('products')
        .select('id, status, stock_quantity, low_stock_threshold, featured')
        .eq('organization_id', profile.organization_id);

      if (error) throw error;

      // Get default price list for the org
      const { data: defaultPriceList } = await supabase
        .from('price_lists')
        .select('id')
        .eq('organization_id', profile.organization_id)
        .eq('is_default', true)
        .eq('is_active', true)
        .maybeSingle();

      // Get prices from the default price list for all products
      let priceMap = new Map<string, number>();
      if (defaultPriceList) {
        const productIds = statsData.map(p => p.id);
        const prices = await fetchInChunks<{ product_id: string; regular_price: number | null; sale_price: number | null }>(
          productIds,
          (chunk) => supabase
            .from('product_price_list_prices')
            .select('product_id, regular_price, sale_price')
            .eq('price_list_id', defaultPriceList.id)
            .in('product_id', chunk),
        );

        prices.forEach(p => {
          // Use sale_price if available, otherwise regular_price
          priceMap.set(p.product_id, p.sale_price || p.regular_price || 0);
        });
      }

      // Calculate stats using default price list prices
      const stats: InventoryStats = {
        total_products: statsData.length,
        active_products: statsData.filter(p => p.status === 'active').length,
        low_stock_products: statsData.filter(p =>
          p.stock_quantity <= p.low_stock_threshold && p.stock_quantity > 0
        ).length,
        out_of_stock_products: statsData.filter(p => p.stock_quantity <= 0).length,
        total_stock_value: statsData.reduce((sum, p) => {
          const price = priceMap.get(p.id) || 0;
          return sum + (price * p.stock_quantity);
        }, 0),
        featured_products: statsData.filter(p => p.featured).length,
        categories_count: 0 // Will be calculated separately
      };

      // Get categories count
      const { count: categoriesCount } = await supabase
        .from('product_categories')
        .select('id', { count: 'exact' })
        .eq('organization_id', profile.organization_id);

      stats.categories_count = categoriesCount || 0;

      set({ stats, statsLoading: false });

    } catch (error) {
      console.error('Error fetching stats:', error);
      set({ statsLoading: false });
    }
  },

  updateStockAbsolute: async (productId: string, newQuantity: number, reason = 'manual_adjustment', notes?: string) => {
    try {
      // Rettifica assoluta zone-aware: allinea zone + stock e registra un solo movimento
      const delta = await WarehouseService.adjustProductStock(productId, newQuantity, reason, notes);

      if (delta !== 0) {
        autoSyncService.triggerSync('stock', productId, 'update', {
          stockChange: delta,
          reason
        });
      }

      return true;
    } catch (error) {
      console.error('Error updating stock (absolute):', error);
      const warehouseError = createWarehouseError(error, 'update stock');
      set({ error: warehouseError.userMessage });
      return false;
    }
  },

  updateStock: async (productId: string, quantity: number, reason = 'manual_adjustment', notes?: string, variationId?: string) => {
    try {
      // NOTE: Manual stock movements do NOT automatically destock from zones.
      // Zone management should be handled separately through the warehouse interface.
      // This keeps stock adjustments and zone operations as separate, explicit actions.

      const { error } = await supabase.rpc('create_stock_movement', {
        p_product_id: productId,
        p_type: quantity > 0 ? 'in' : 'out',
        p_quantity: Math.abs(quantity),
        p_variation_id: variationId || null,
        p_reason: reason,
        p_notes: notes || `Stock adjustment: ${quantity > 0 ? '+' : ''}${quantity}`
      });

      if (error) throw error;

      // Don't auto-refresh products list - let the calling component handle refresh
      // This prevents flickering and "product not found" issues

      // Trigger auto-sync for stock update
      autoSyncService.triggerSync('stock', productId, 'update', {
        stockChange: quantity,
        reason: reason,
        variationId: variationId
      });

      return true;

    } catch (error) {
      console.error('Error updating stock:', error);
      const warehouseError = createWarehouseError(error, 'update stock');
      set({ error: warehouseError.userMessage });
      return false;
    }
  },

  updateVariation: async (variationId: string, data: Partial<ProductVariation>) => {
    try {
      // Update variation in database
      const { error: updateError } = await supabase
        .from('product_variations')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', variationId);

      if (updateError) throw updateError;

      // Get the product_id for this variation to trigger sync
      const { data: variation, error: fetchError } = await supabase
        .from('product_variations')
        .select('product_id')
        .eq('id', variationId)
        .single();

      if (!fetchError && variation?.product_id) {
        // Trigger auto-sync for the parent product
        autoSyncService.triggerSync('product', variation.product_id, 'update', {
          variationUpdated: variationId
        });
      }

      return true;

    } catch (error) {
      console.error('Error updating variation:', error);
      const warehouseError = createWarehouseError(error, 'update variation');
      set({ error: warehouseError.userMessage });
      return false;
    }
  },

  importProducts: async (data: ProductImportData[]) => {
    // TODO: Implement bulk import logic
    throw new Error('Import functionality not yet implemented');
  },

  exportProducts: async (filters?: ProductFilter) => {
    // TODO: Implement export logic
    throw new Error('Export functionality not yet implemented');
  },

  reset: () => {
    set(initialState);
  }
}));
